import React, { useState, useEffect, useCallback } from 'react'

import { Search as SearchIcon } from 'lucide-react'
import { useDebouncedCallback } from 'use-debounce'

import { Input } from '@/shared/components/atoms/Input'

interface SearchProps {
	/**
	 * Current search value
	 */
	value: string
	/**
	 * Callback fired when search value changes (debounced)
	 */
	onChange: (value: string) => void
	/**
	 * Placeholder text
	 * @default "Search"
	 */
	placeholder?: string
	/**
	 * Debounce delay in milliseconds
	 * @default 500
	 */
	debounceMs?: number
	/**
	 * Whether the search input is disabled
	 * @default false
	 */
	disabled?: boolean
	/**
	 * Additional CSS classes for the container
	 */
	className?: string
}

/**
 * Reusable Search component with built-in debouncing
 *
 * @example
 * ```tsx
 * const [search, setSearch] = useState('')
 *
 * <Search
 *   value={search}
 *   onChange={setSearch}
 *   placeholder="Search zones..."
 * />
 * ```
 */
export const Search: React.FC<SearchProps> = ({
	value,
	onChange,
	placeholder = 'Search',
	debounceMs = 500,
	disabled = false,
	className = '',
}) => {
	const [internalValue, setInternalValue] = useState(value)

	useEffect(() => {
		setInternalValue(value)
	}, [value])

	const debouncedOnChange = useDebouncedCallback((newValue: string) => {
		onChange(newValue)
	}, debounceMs)

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const newValue = e.target.value
			setInternalValue(newValue)
			debouncedOnChange(newValue)
		},
		[debouncedOnChange]
	)

	return (
		<div className={`relative flex-1 max-w-md ${className}`}>
			<SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
			<Input
				type="search"
				placeholder={placeholder}
				value={internalValue}
				onChange={handleChange}
				disabled={disabled}
				className="pl-9 py-2.5 rounded-lg text-sm placeholder:text-muted-foreground w-full max-w-none"
			/>
		</div>
	)
}
